import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../constants";

/**
 * Fisher-Yates shuffle (returns a NEW array; original is untouched).
 */
function shuffled(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Public-facing section listing opt-in lifetime backers.
 * Rendered on the LandingPage below the Support CTA.
 *
 * Tile order is shuffled on every page load so different backers get prime
 * top-left placement on each visit — community-friendly fairness.
 */
export default function FoundingBackersWall() {
  const [backers, setBackers] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/backers`)
      .then((res) => setBackers(shuffled(res.data.backers || [])))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;
  if (backers.length === 0) {
    return (
      <section id="founding-backers" className="py-16 px-4" data-testid="founding-backers-empty">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3">Founding Backers</h2>
          <p className="text-[#E8E4DC]">
            Be the first name on this wall. Back Top Screen&trade; for $39 and earn permanent visibility here.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="founding-backers" className="py-20 px-4" data-testid="founding-backers">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-block text-xs font-mono px-3 py-1 rounded-full bg-[#A3E635]/15 neon-green-pulse mb-3" data-testid="lifetime-supporters-badge">Lifetime Supporters</span>
          <p className="text-[#E8E4DC] mb-2" data-testid="founding-backers-heading">Founding Backers</p>
          <p className="text-[#E8E4DC]">
            {backers.length} {backers.length === 1 ? "backer" : "backers"} powering Top Screen&trade; and 713 C&amp;D innovations.
          </p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {backers.map((b, i) => {
            const initials = (b.display_name || "?")
              .split(/\s+/)
              .map((part) => part[0])
              .filter(Boolean)
              .slice(0, 2)
              .join("")
              .toUpperCase();
            return (
              <div
                key={i}
                className="card-glass p-4 flex items-center gap-3 border border-white/5 hover:border-[#A3E635]/30 transition-colors"
                data-testid={`backer-tile-${i}`}
              >
                {b.picture ? (
                  <img
                    src={b.picture}
                    alt=""
                    className="w-10 h-10 rounded-full object-cover shrink-0"
                    referrerPolicy="no-referrer"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[#A3E635]/15 flex items-center justify-center shrink-0">
                    <span className="text-[#A3E635] font-bold text-sm">{initials || "TS"}</span>
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{b.display_name}</p>
                  {b.joined_at && (
                    <p className="text-[#E8E4DC]/60 text-xs">Joined {new Date(b.joined_at).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
